/**
 * YellowHighlight — wraps text with an animated yellow underscore.
 * The primary signature visual element for the TEX marketing site.
 *
 * Variants:
 *   - pulse: 3px bar pulses opacity (0.6→1.0) on a 2s loop
 *   - static: solid 3px bar, no animation
 *   - draw: underscore animates left-to-right on first scroll into view
 *
 * On hover: the underscore expands from 3px to full background (0.3s ease-out).
 */

'use client';

import React, { useRef } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils/utils';

type HighlightVariant = 'pulse' | 'static' | 'draw';

interface YellowHighlightProps {
  children: React.ReactNode;
  variant?: HighlightVariant;
  /** Stagger delay in ms (used with the draw variant) */
  delay?: number;
  className?: string;
}

const variantClass: Record<HighlightVariant, string> = {
  pulse: 'mktg-yh mktg-yh--pulse',
  static: 'mktg-yh mktg-yh--static',
  draw: 'mktg-yh mktg-yh--draw'
};

export function YellowHighlight({
  children,
  variant = 'pulse',
  delay = 0,
  className
}: YellowHighlightProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const prefersReduced = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.5 });

  // For draw variant, trigger only when in view
  const isActive = variant === 'draw' ? inView : true;

  // If user prefers reduced motion, fall back to static
  const resolvedVariant = prefersReduced ? 'static' : variant;

  return (
    <span
      ref={ref}
      className={cn(
        variantClass[resolvedVariant],
        isActive && 'mktg-yh--active',
        className
      )}
      style={
        delay > 0
          ? ({ '--mktg-yh-delay': `${delay}ms` } as React.CSSProperties)
          : undefined
      }
    >
      {children}
    </span>
  );
}
